import { AfterViewInit, Component, DestroyRef, ElementRef, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Gridster, GridsterConfig, GridsterItem, GridsterItemConfig } from 'angular-gridster2';
import { TokenService } from 'authentication/services/token.service';
import { VisualDashboardItemEditModalComponent } from 'dashboard/components/visuals-dashboard/visual-dashboard-item-edit-modal/visual-dashboard-item-edit-modal.component';
import { VisualDashboardItemPickerModalComponent } from 'dashboard/components/visuals-dashboard/visual-dashboard-item-picker-modal/visual-dashboard-item-picker-modal.component';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { DashboardListWidget, ImageTrackerDistributionSettings } from 'dashboard/interfaces/visuals/list-widget.interface';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { cloneDeep } from 'lodash-es';
import { ModalService } from 'shared/services';
import { VisualDashboardItemWrapperComponent } from './visual-dashboard-item-wrapper/visual-dashboard-item-wrapper.component';

@Component({
    selector: 'app-visuals-dashboard',
    imports: [Gridster, GridsterItem, VisualDashboardItemWrapperComponent, FormsModule, TranslateModule],
    templateUrl: './visuals-dashboard.component.html',
    styleUrl: './visuals-dashboard.component.scss',
})
export class VisualsDashboardComponent implements AfterViewInit {
    private readonly widgetWidth = 350;
    private readonly widgetHeight = 350;
    private readonly marginInPx = 20;
    private readonly mainContainerPadding = 20;
    private readonly maxAllowedCols = 10;

    private modalService = inject(ModalService);
    private destroyRef = inject(DestroyRef);
    private tokenService = inject(TokenService);

    private capturedStateBeforeEdit = signal<DashboardWidget[] | null>(null);
    private editModeBaseState = signal<DashboardWidget[] | null>(null);
    private initialLoadDone = false;
    private resizeObserver: ResizeObserver | null = null;

    private gridster = viewChild(Gridster);
    private mainContainer = viewChild<ElementRef<HTMLElement>>('mainContainer');
    private gridContainer = viewChild<ElementRef<HTMLElement>>('gridContainer');

    gridCols = signal(1);
    dashboardContainerWidth = signal(0);
    isEditMode = signal(false);
    options = signal<GridsterConfig>({
        itemChangeCallback: this.itemChange.bind(this),
        gridSizeChangedCallback: this.changeContainerHeight.bind(this),
        initCallback: this.changeContainerHeight.bind(this),
        gridType: 'fixed',
        draggable: {
            enabled: false,
        },
        resizable: {
            enabled: false,
        },
        maxCols: 10,
        minCols: 1,
        fixedColWidth: this.widgetWidth,
        fixedRowHeight: this.widgetHeight,
        margin: this.marginInPx,
        pushItems: true,
        keepFixedHeightInMobile: true,
        swap: true,
        mobileBreakpoint: 640,
        disableWindowResize: true,
        disablePushOnResize: false,
        pushResizeItems: true,
        disableWarnings: true,
        scrollToNewItems: true,
        outerMargin: false,
        displayGrid: 'none',
    });

    constructor(public visualsDashboardService: VisualsDashboardService) {
        this.destroyRef.onDestroy(() => {
            this.resizeObserver?.disconnect();
        });

        effect(() => {
            const token = this.tokenService.iceDashboardToken();
            if (token && !this.initialLoadDone) {
                this.initialLoadDone = true;
                untracked(() => {
                    this.visualsDashboardService.loadedDashboardData.set(false);
                    this.visualsDashboardService.loadWidgetsList$.next();
                    this.visualsDashboardService.loadDashboardState$.next();
                });
            }
        });

        effect(() => {
            const editMode = this.isEditMode();
            untracked(() => {
                if (editMode) {
                    this.handleEnterEditMode();
                } else {
                    this.handleExitEditMode();
                }
            });
        });

        effect(() => {
            const dashboardState = this.visualsDashboardService.mappedDashboardState();

            untracked(() => {
                if (!this.isEditMode()) {
                    const adjustedWidgets = cloneDeep(dashboardState).map((widget) => {
                        this.resetWidgetToFit(widget);
                        return widget;
                    });

                    const repositionedWidgets = this.recalculatePositionsForCurrentGrid(adjustedWidgets);

                    this.visualsDashboardService.dashboard.set(repositionedWidgets);
                }
            });
        });
    }

    ngAfterViewInit(): void {
        this.calculateGridDimensions();
        this.setupContainerResizeObserver();
        this.setGridsterOverflow('visible');
    }

    itemChange(item: GridsterItemConfig, itemComponent: any): void {
        this.updateEditModeBaseState();
    }

    openItemAddingModal(): void {
        const modalData = { type: 'side' };
        const reference = this.modalService.openModal(VisualDashboardItemPickerModalComponent, modalData);
        reference.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (
                result:
                    | { widget: DashboardListWidget; selectedSize: VisualsDashboardWidgetSizingEnum; selectedPeriod: number | null }[]
                    | undefined
                    | null,
            ) => {
                if (result && Array.isArray(result) && result.length > 0) {
                    result.forEach(({ widget, selectedSize, selectedPeriod }) => {
                        this.addItem(widget, selectedSize, selectedPeriod);
                    });
                }
                this.visualsDashboardService.selectedWidgetsForAdding.set([]);
            },
        });
    }

    openItemEditModal(item: DashboardWidget): void {
        const modalData = { type: 'side', item: item };
        const reference = this.modalService.openModal(VisualDashboardItemEditModalComponent, modalData);
        reference.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (
                result:
                    | {
                          selectedSize: VisualsDashboardWidgetSizingEnum;
                          selectedPeriod: number | null;
                          selectedDistributorSettings: ImageTrackerDistributionSettings | null;
                      }
                    | undefined
                    | null,
            ) => {
                if (!result || !Object.values(VisualsDashboardWidgetSizingEnum).includes(result.selectedSize)) {
                    return;
                }

                const currentSize = this.visualsDashboardService.getSizeBasedOnRowsAndCols(item.rows, item.cols);
                const sizeChanged = currentSize !== result.selectedSize;
                const periodChanged = item.selectedPeriodOptionValue !== result.selectedPeriod;
                const distributorSettingsChanged =
                    JSON.stringify(item.imageTrackerDistributionSettings) !== JSON.stringify(result.selectedDistributorSettings);

                if (!sizeChanged && !periodChanged && !distributorSettingsChanged) {
                    return;
                }

                if (sizeChanged) {
                    this.resizeItem(item, result.selectedSize, result.selectedPeriod);
                } else if (periodChanged) {
                    this.updateItemPeriod(item, result.selectedPeriod);
                } else if (distributorSettingsChanged && result.selectedDistributorSettings) {
                    this.updateItemDistributorSettings(item, result.selectedDistributorSettings);
                }
            },
        });
    }

    enterEditMode(): void {
        this.isEditMode.set(true);
    }

    updateItemDistributorSettings(item: DashboardWidget, settings: ImageTrackerDistributionSettings): void {
        const targetHtmlId = item.htmlId;

        this.visualsDashboardService.dashboard.update((currentDashboard) =>
            currentDashboard.map((widget) =>
                widget.htmlId === targetHtmlId
                    ? {
                          ...widget,
                          imageTrackerDistributionSettings: settings,
                      }
                    : widget,
            ),
        );

        this.updateEditModeBaseState();
    }

    handleEnterEditMode(): void {
        this.capturedStateBeforeEdit.set(cloneDeep(this.visualsDashboardService.dashboard()));
        this.editModeBaseState.set(cloneDeep(this.visualsDashboardService.dashboard()));

        this.options.set({
            ...this.options(),
            draggable: {
                enabled: true,
            },
            displayGrid: 'always',
        });

        this.setGridsterOverflow('auto');
    }

    handleExitEditMode(): void {
        this.options.set({
            ...this.options(),
            draggable: {
                enabled: false,
            },
            displayGrid: 'none',
        });

        this.setGridsterOverflow('visible');
    }

    getStarted() {
        this.enterEditMode();
        this.openItemAddingModal();
    }

    exitEditMode(shouldSaveData: boolean): void {
        this.isEditMode.set(false);
        this.editModeBaseState.set(null);

        if (shouldSaveData) {
            const currentDashboardState = this.visualsDashboardService.dashboard();
            this.visualsDashboardService.saveDashboardState(currentDashboardState);
        } else {
            const capturedState = this.capturedStateBeforeEdit();
            if (capturedState) {
                this.restoreDashboardState(capturedState);
            }
        }

        this.capturedStateBeforeEdit.set(null);
    }

    removeItem(item: DashboardWidget): void {
        this.visualsDashboardService.dashboard.update((currentDashboard) => {
            const itemIndex = currentDashboard.indexOf(item);
            const itemRows = item.rows;

            if (this.gridCols() === 1 && itemIndex !== -1) {
                const updatedDashboard = currentDashboard.filter((dashboardItem) => dashboardItem !== item);

                updatedDashboard.slice(itemIndex).forEach((widget) => {
                    widget.y -= itemRows;
                });

                return updatedDashboard;
            }

            return currentDashboard.filter((dashboardItem) => dashboardItem !== item);
        });

        this.updateEditModeBaseState();
    }

    addItem(item: DashboardListWidget, selectedSize: VisualsDashboardWidgetSizingEnum, selectedPeriod: number | null): void {
        const newItem: DashboardWidget = this.visualsDashboardService.mapDashboardListWidgetToDashboardWidget(
            item,
            selectedSize,
            true,
            selectedPeriod,
        );

        const api = this.gridster()?.api;

        this.visualsDashboardService.dashboard.update((currentDashboard) => {
            const position = api?.getFirstPossiblePosition?.(newItem) ?? { x: 0, y: 0 };
            newItem.x = position.x;
            newItem.y = position.y;
            newItem.iceDashboardWidget.positionX = position.x;
            newItem.iceDashboardWidget.positionY = position.y;
            return [...currentDashboard, newItem];
        });

        this.updateEditModeBaseState();
    }

    private setGridsterOverflow(value: string): void {
        const gridsterElement = this.gridContainer()?.nativeElement?.querySelector('gridster');
        if (gridsterElement) {
            (gridsterElement as HTMLElement).style.overflow = value;
        }
    }

    private setupContainerResizeObserver(): void {
        const mainContainerElement = this.mainContainer()?.nativeElement;
        if (!mainContainerElement) {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            const previousCols = this.gridCols();
            this.calculateGridDimensions();
            const newCols = this.gridCols();

            if (previousCols !== newCols) {
                const sourceWidgets = this.isEditMode() ? this.editModeBaseState()! : this.visualsDashboardService.mappedDashboardState();

                const adjustedWidgets = cloneDeep(sourceWidgets).map((widget) => {
                    this.resetWidgetToFit(widget);
                    return widget;
                });

                const repositionedWidgets = this.recalculatePositionsForCurrentGrid(adjustedWidgets);
                this.visualsDashboardService.dashboard.set(repositionedWidgets);
            }
        });

        this.resizeObserver.observe(mainContainerElement);
    }

    private updateEditModeBaseState(): void {
        if (this.isEditMode()) {
            this.editModeBaseState.set(cloneDeep(this.visualsDashboardService.dashboard()));
        }
    }

    private updateItemPeriod(item: DashboardWidget, selectedPeriod: number | null): void {
        const targetHtmlId = item.htmlId;

        this.visualsDashboardService.dashboard.update((currentDashboard) =>
            currentDashboard.map((widget) =>
                widget.htmlId === targetHtmlId
                    ? {
                          ...widget,
                          selectedPeriodOptionValue: selectedPeriod,
                      }
                    : widget,
            ),
        );

        this.updateEditModeBaseState();
    }

    private restoreDashboardState(capturedState: DashboardWidget[]): void {
        const api = this.gridster()?.api;

        if (!api) {
            this.visualsDashboardService.dashboard.set(capturedState);
            return;
        }

        capturedState.forEach((originalWidget) => {
            const gridsterItemComponent = api.getItemComponent?.(originalWidget) as any;

            if (gridsterItemComponent) {
                const gridsterInstance = gridsterItemComponent.gridster;

                const internalGridsterItem = gridsterItemComponent.item();

                gridsterItemComponent.$item.cols = originalWidget.cols;
                gridsterItemComponent.$item.rows = originalWidget.rows;
                gridsterItemComponent.$item.x = originalWidget.x;
                gridsterItemComponent.$item.y = originalWidget.y;
                internalGridsterItem.cols = originalWidget.cols;
                internalGridsterItem.rows = originalWidget.rows;
                internalGridsterItem.x = originalWidget.x;
                internalGridsterItem.y = originalWidget.y;
                internalGridsterItem.originalCols = originalWidget.originalCols;
                internalGridsterItem.originalRows = originalWidget.originalRows;
                internalGridsterItem.selectedSize = originalWidget.selectedSize;
                internalGridsterItem.selectedPeriodOptionValue = originalWidget.selectedPeriodOptionValue;

                if (originalWidget.iceDashboardWidget) {
                    internalGridsterItem.iceDashboardWidget.sizeType = originalWidget.iceDashboardWidget.sizeType;
                    internalGridsterItem.iceDashboardWidget.sizeTypeId = originalWidget.iceDashboardWidget.sizeTypeId;
                    internalGridsterItem.iceDashboardWidget.settings = originalWidget.iceDashboardWidget.settings;
                }

                gridsterItemComponent.setSize();

                if (gridsterInstance) {
                    gridsterInstance.updateGrid();
                }
            }
        });

        this.visualsDashboardService.dashboard.set(capturedState);
    }

    private resizeItem(item: DashboardWidget, size: VisualsDashboardWidgetSizingEnum, selectedPeriod?: number | null): void {
        const { rows, cols } = this.visualsDashboardService.getRowsAndColsBySize(size);
        const api = this.gridster()?.api;

        if (!api) {
            return;
        }

        const gridsterItemComponent = api.getItemComponent?.(item) as any;
        const gridsterInstance = gridsterItemComponent?.gridster;

        if (!gridsterInstance || !gridsterItemComponent) {
            return;
        }

        const internalGridsterItem = gridsterItemComponent.item();

        gridsterItemComponent.$item.cols = cols;
        gridsterItemComponent.$item.rows = rows;
        internalGridsterItem.cols = cols;
        internalGridsterItem.rows = rows;
        internalGridsterItem.originalCols = cols;
        internalGridsterItem.originalRows = rows;

        const allItems = this.visualsDashboardService.rawWidgetsList();
        const widgetId = item.iceDashboardWidget?.widgetId ?? item.widgetId;
        const dashboardWidget = allItems.find((widget) => widget.widgetId === widgetId);
        const dimension = this.visualsDashboardService.mapSizeToDimensions(size);
        const sizeByDimension = dashboardWidget?.widgetSizes.find((s) => s.type === dimension);

        if (sizeByDimension) {
            internalGridsterItem.iceDashboardWidget.sizeType = sizeByDimension;
            internalGridsterItem.iceDashboardWidget.sizeTypeId = sizeByDimension.sizeTypeId;
            internalGridsterItem.selectedSize = size;
        }

        this.resolveCollisions(gridsterInstance, gridsterItemComponent);

        gridsterItemComponent.setSize();
        gridsterInstance.updateGrid();

        const targetHtmlId = item.htmlId;

        this.visualsDashboardService.dashboard.update((currentDashboard) =>
            currentDashboard.map((widget) =>
                widget.htmlId === targetHtmlId
                    ? {
                          ...widget,
                          rows,
                          cols,
                          originalRows: rows,
                          originalCols: cols,
                          selectedSize: size,
                          selectedPeriodOptionValue: selectedPeriod ?? widget.selectedPeriodOptionValue,
                          iceDashboardWidget: {
                              ...widget.iceDashboardWidget,
                              sizeType: sizeByDimension ?? widget.iceDashboardWidget.sizeType,
                              sizeTypeId: sizeByDimension?.sizeTypeId ?? widget.iceDashboardWidget.sizeTypeId,
                          },
                      }
                    : widget,
            ),
        );

        this.updateEditModeBaseState();
    }

    private resolveCollisions(gridsterInstance: any, excludeItem: any): void {
        const allItems: any[] = gridsterInstance.grid || [];

        const sortedItems = allItems
            .filter((itemComponent: any) => itemComponent !== excludeItem)
            .sort((a: any, b: any) => {
                if (a.$item.y !== b.$item.y) {
                    return a.$item.y - b.$item.y;
                }
                return a.$item.x - b.$item.x;
            });

        sortedItems.forEach((itemComponent: any) => {
            const collisions = gridsterInstance.findItemsWithItem?.(itemComponent.$item) || [];
            const hasCollision = collisions.some((c: any) => c !== itemComponent);

            if (hasCollision) {
                const newPosition = this.findNextValidPosition(gridsterInstance, itemComponent, allItems);

                itemComponent.$item.x = newPosition.x;
                itemComponent.$item.y = newPosition.y;
                itemComponent.item.x = newPosition.x;
                itemComponent.item.y = newPosition.y;
                itemComponent.setSize();
            }
        });
    }

    private findNextValidPosition(gridsterInstance: any, itemComponent: any, allItems: any[]): { x: number; y: number } {
        const itemCols = itemComponent.$item.cols;
        const gridCols = gridsterInstance.columns;

        for (let y = 0; y < 100; y++) {
            for (let x = 0; x <= gridCols - itemCols; x++) {
                const testPosition = {
                    ...itemComponent.$item,
                    x,
                    y,
                };

                const hasCollision = allItems.some((other: any) => {
                    if (other === itemComponent) {
                        return false;
                    }

                    return gridsterInstance.checkCollisionTwoItems?.(testPosition, other.$item);
                });

                if (!hasCollision) {
                    return { x, y };
                }
            }
        }

        return { x: 0, y: itemComponent.$item.y + 1 };
    }

    private changeContainerHeight(gridsterInstance: any): void {
        const gridContainerHeight =
            gridsterInstance.rows * gridsterInstance.$options.fixedRowHeight +
            (gridsterInstance.rows - 1) * gridsterInstance.$options.margin;

        const container = this.gridContainer()?.nativeElement;
        if (container) {
            container.style.height = `${gridContainerHeight}px`;
        }
    }

    private calculateGridDimensions(): void {
        const mainContainerElement = this.mainContainer()?.nativeElement;
        if (!mainContainerElement) {
            console.error('Main container not found');
            return;
        }

        const mainContainerWidth = mainContainerElement.offsetWidth;
        const availableWidth = mainContainerWidth - this.mainContainerPadding;

        const calculatedCols = Math.min(
            this.maxAllowedCols,
            Math.floor((availableWidth + this.marginInPx) / (this.widgetWidth + this.marginInPx)),
        );

        this.gridCols.set(calculatedCols);

        let containerWidth: number;
        if (calculatedCols === 1) {
            containerWidth = availableWidth;
        } else {
            const idealWidth = calculatedCols * (this.widgetWidth + this.marginInPx) - this.marginInPx;
            containerWidth = Math.min(idealWidth, availableWidth);
        }

        this.dashboardContainerWidth.set(containerWidth);

        const twoColumnThreshold = 2 * (this.widgetWidth + this.marginInPx) - this.marginInPx;

        this.options.update((currentOptions) => ({
            ...currentOptions,
            maxCols: calculatedCols,
            minCols: calculatedCols,
            mobileBreakpoint: twoColumnThreshold,
        }));
    }

    private resetWidgetToFit(widget: DashboardWidget): void {
        const gridCols = this.gridCols();

        if (gridCols === 1) {
            widget.cols = 1;
            widget.rows = 1;
        } else if (widget.originalRows && widget.originalCols) {
            widget.rows = widget.originalRows;
            widget.cols = widget.originalCols;
        }

        if (widget.cols > gridCols) {
            widget.cols = gridCols;
        }
    }

    private recalculatePositionsForCurrentGrid(widgets: DashboardWidget[]): DashboardWidget[] {
        const gridCols = this.gridCols();

        const sortedWidgets = [...widgets].sort((a, b) => {
            if (a.y !== b.y) return a.y - b.y;
            return a.x - b.x;
        });

        const occupiedCells: boolean[][] = [];

        const ensureRowExists = (rowIndex: number) => {
            while (occupiedCells.length <= rowIndex) {
                occupiedCells.push(new Array(gridCols).fill(false));
            }
        };

        const isPositionValid = (x: number, y: number, cols: number, rows: number): boolean => {
            if (x < 0 || x + cols > gridCols) return false;
            ensureRowExists(y + rows - 1);

            for (let row = y; row < y + rows; row++) {
                for (let col = x; col < x + cols; col++) {
                    if (occupiedCells[row][col]) return false;
                }
            }
            return true;
        };

        const occupyPosition = (x: number, y: number, cols: number, rows: number) => {
            ensureRowExists(y + rows - 1);
            for (let row = y; row < y + rows; row++) {
                for (let col = x; col < x + cols; col++) {
                    occupiedCells[row][col] = true;
                }
            }
        };

        const findFirstValidPosition = (cols: number, rows: number): { x: number; y: number } => {
            for (let y = 0; y < 1000; y++) {
                for (let x = 0; x <= gridCols - cols; x++) {
                    if (isPositionValid(x, y, cols, rows)) {
                        return { x, y };
                    }
                }
            }
            return { x: 0, y: 0 };
        };

        sortedWidgets.forEach((widget) => {
            if (isPositionValid(widget.x, widget.y, widget.cols, widget.rows)) {
                occupyPosition(widget.x, widget.y, widget.cols, widget.rows);
            } else {
                const newPos = findFirstValidPosition(widget.cols, widget.rows);
                widget.x = newPos.x;
                widget.y = newPos.y;
                occupyPosition(newPos.x, newPos.y, widget.cols, widget.rows);
            }
        });

        return sortedWidgets;
    }
}
